import {
  CATEGORY_META,
  categoryOf,
  isNotifyCategory,
  isNotifyKind,
  KIND_META,
  kindsIn,
  NOTIFY_CATEGORIES,
  type NotifyCategory,
  type NotifyKind,
  type NotifyVoice,
} from '@alphadesk/ontology/objects/notification.ts';
import type { WireKindTally } from '@alphadesk/web/contract.ts';
import {
  ArrowRightLeft,
  Ban,
  Bot,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCheck,
  Circle,
  ClipboardCheck,
  Crosshair,
  FileText,
  Flag,
  Gift,
  Handshake,
  KeyRound,
  Layers,
  Link2Off,
  Lock,
  LockOpen,
  type LucideIcon,
  MessageSquare,
  Newspaper,
  Radio,
  ScrollText,
  Send,
  Shield,
  ShieldMinus,
  ShieldOff,
  SlidersHorizontal,
  Sparkles,
  Sunrise,
  Ticket,
  TriangleAlert,
  UserCheck,
  UserPlus,
  UsersRound,
  Zap,
} from 'lucide-react';
import { useQueryState } from './router.ts';

const KIND_ICON: Record<NotifyKind, LucideIcon> = {
  'new-recommendation': Sparkles,
  'recommendation-revised': SlidersHorizontal,
  'recommendation-closed': Flag,
  'recommendation-roll-due': CalendarClock,
  'event-ahead': CalendarClock,
  'own-level-reached': Crosshair,
  'stop-trailed': Lock,
  'recommendation-rolled': ArrowRightLeft,

  'fallback-fired': Zap,
  'fallback-misfired': TriangleAlert,

  'entry-filled': Check,
  'exit-filled': CheckCheck,
  'entry-cancelled': Ban,

  'fallback-armed': Shield,
  'fallback-unguarded': ShieldOff,
  'fallback-lapsed': LockOpen,
  'fallback-released': ShieldMinus,
  'position-roll-due': CalendarClock,
  'position-rolled': ArrowRightLeft,
  'roll-failed': TriangleAlert,

  'autopilot-took': Bot,
  'autopilot-exited': Bot,
  'autopilot-skipped': Bot,
  'square-off-failed': TriangleAlert,

  'entry-failed': TriangleAlert,
  'exit-failed': TriangleAlert,

  'kite-session': KeyRound,

  'access-request': UserPlus,
  'access-decision': UserCheck,
  'analyst-request': ScrollText,
  'analyst-decision': ClipboardCheck,
  'follow-request': Handshake,
  'follow-decision': UserCheck,
  'follow-invite-used': Ticket,
  'invite-admitted': Ticket,
  feedback: MessageSquare,
  'channel-draft': Newspaper,
  'channel-post': Newspaper,
  'telegram-unlinked': Send,
  'funds-month': Layers,
  'pnl-link': Link2Off,
  'broker-ready': KeyRound,
  'invite-decided': Ticket,
  'invite-earned': Gift,
  'morning-digest': Sunrise,
  'market-calendar': CalendarDays,
  'nse-circular': FileText,
  'participant-positions': UsersRound,
  news: Radio,
};

export const iconOf = (kind: string): LucideIcon => (isNotifyKind(kind) ? KIND_ICON[kind] : Circle);

export const alarmInk = (row: { voice: NotifyVoice }): string | null =>
  row.voice === 'alarm' ? 'text-warn' : null;

export const alarmVarOf = (kind: NotifyKind): string | undefined =>
  KIND_META[kind].voice === 'alarm' ? 'var(--color-warn)' : undefined;

export interface InboxLens {
  category: NotifyCategory | null;
  kind: NotifyKind | null;
  unreadOnly: boolean;
}

export const NO_LENS: InboxLens = { category: null, kind: null, unreadOnly: false };

export const isLensed = (lens: InboxLens): boolean =>
  lens.category !== null || lens.kind !== null || lens.unreadOnly;

export const kindsOf = (lens: InboxLens): NotifyKind[] | undefined =>
  lens.kind !== null ? [lens.kind] : lens.category !== null ? kindsIn(lens.category) : undefined;

// Must match the server's `where` for the same lens.
export function matchesLens(
  row: { kind: string; readAt: string | null },
  lens: InboxLens,
): boolean {
  if (lens.unreadOnly && row.readAt !== null) return false;
  if (lens.kind !== null) return row.kind === lens.kind;
  if (lens.category !== null) return categoryOf(row.kind) === lens.category;
  return true;
}

// Owns only these keys: Trades' lens shares the query string.
const QUERY_KEYS = ['ncat', 'nkind', 'nunread'] as const;

function parseLens(search: string): InboxLens {
  const q = new URLSearchParams(search);
  const rawKind = q.get('nkind');
  const kind = rawKind !== null && isNotifyKind(rawKind) ? rawKind : null;
  const rawCategory = q.get('ncat');
  const category = rawCategory !== null && isNotifyCategory(rawCategory) ? rawCategory : null;
  return {
    // A kind implies its category, overriding a disagreeing `ncat`.
    category: kind !== null ? categoryOf(kind) : category,
    kind,
    unreadOnly: q.get('nunread') === '1',
  };
}

function serialiseLens(lens: InboxLens, search: string): string {
  const q = new URLSearchParams(search);
  for (const key of QUERY_KEYS) q.delete(key);
  if (lens.kind !== null) q.set('nkind', lens.kind);
  else if (lens.category !== null) q.set('ncat', lens.category);
  if (lens.unreadOnly) q.set('nunread', '1');
  return q.toString();
}

export const useInboxLens = () => useQueryState(parseLens, serialiseLens);

export interface CategoryCount {
  category: NotifyCategory;
  label: string;
  blurb: string;
  total: number;
  unread: number;
  kinds: KindCount[];
}

interface KindCount {
  kind: NotifyKind;
  label: string;
  total: number;
  unread: number;
}

// Never-sent categories draw no chip (unlike books, which persist when filtered empty).
export function categoryCounts(tally: readonly WireKindTally[]): CategoryCount[] {
  const byKind = new Map(tally.map((row) => [row.kind, row]));
  return NOTIFY_CATEGORIES.flatMap((category) => {
    const kinds = kindsIn(category).flatMap((kind) => {
      const counted = byKind.get(kind);
      return counted === undefined || counted.total === 0
        ? []
        : [
            {
              kind,
              label: KIND_META[kind].label,
              total: counted.total,
              unread: counted.unread,
            },
          ];
    });
    if (kinds.length === 0) return [];
    return [
      {
        category,
        label: CATEGORY_META[category].label,
        blurb: CATEGORY_META[category].blurb,
        total: kinds.reduce((sum, entry) => sum + entry.total, 0),
        unread: kinds.reduce((sum, entry) => sum + entry.unread, 0),
        kinds,
      },
    ];
  });
}

export const tallyTotals = (
  tally: readonly WireKindTally[],
): { total: number; unread: number } => ({
  total: tally.reduce((sum, row) => sum + row.total, 0),
  unread: tally.reduce((sum, row) => sum + row.unread, 0),
});
